import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Copy, LogOut, RefreshCcw, UserPlus, Check } from "lucide-react";
import { useHouseholdMode } from "@/hooks/useHouseholdMode";

export default function HouseholdCard() {
  const [state, setState] = useState(null); // { household, is_owner, is_member }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("view"); // view | create | join
  const [name, setName] = useState("Our household");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const { enabled: hhMode, toggle: toggleMode } = useHouseholdMode();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/household/me");
      setState(data);
      if (!data.is_member) toggleMode(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/household/create", { name });
      toast.success("Household created — share the invite code");
      await load();
      setMode("view");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create household");
    }
    setBusy(false);
  };

  const join = async () => {
    setBusy(true);
    try {
      await api.post("/household/join", { invite_code: code });
      toast.success("Joined household 🎉");
      await load();
      setMode("view");
      toggleMode(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join");
    }
    setBusy(false);
  };

  const leave = async () => {
    if (!window.confirm("Leave this household? You'll go back to viewing your own data only.")) return;
    setBusy(true);
    try {
      await api.post("/household/leave");
      toggleMode(false);
      toast.info("Left household");
      await load();
    } finally { setBusy(false); }
  };

  const rotate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/household/rotate_code");
      toast.success(`New invite code: ${data.invite_code}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not rotate");
    }
    setBusy(false);
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  if (loading) return null;

  const hh = state?.household;

  return (
    <div data-testid="household-card" className="mt-4 rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-slate-400" strokeWidth={1.75} />
          <div className="text-sm font-semibold text-slate-900">Family plan</div>
        </div>
        {hh && (
          <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-semibold">Active</span>
        )}
      </div>

      {!hh && mode === "view" && (
        <>
          <p className="mt-2 text-xs text-slate-500 leading-relaxed">Share MoneyWise with a partner. See combined spending, budgets and goals. Up to 5 members.</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button data-testid="household-create-open" onClick={() => setMode("create")} className="rounded-full py-2.5 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors flex items-center justify-center gap-1.5">
              <UserPlus size={14} /> Create
            </button>
            <button data-testid="household-join-open" onClick={() => setMode("join")} className="rounded-full py-2.5 text-sm font-semibold bg-slate-100 hover:bg-slate-200 text-slate-900 transition-colors">
              Join with code
            </button>
          </div>
        </>
      )}

      {!hh && mode === "create" && (
        <div className="mt-3 space-y-3">
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Household name</div>
            <input
              data-testid="household-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="e.g. The Morgans"
            />
          </label>
          <div className="flex gap-2">
            <button data-testid="household-create-btn" onClick={create} disabled={busy || !name.trim()} className="flex-1 rounded-full py-2.5 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60">
              {busy ? "Creating…" : "Create household"}
            </button>
            <button onClick={() => setMode("view")} className="rounded-full px-4 text-sm font-medium text-slate-500 hover:text-slate-900">Cancel</button>
          </div>
        </div>
      )}

      {!hh && mode === "join" && (
        <div className="mt-3 space-y-3">
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Invite code</div>
            <input
              data-testid="household-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder="FAM-XXXXXX"
            />
          </label>
          <div className="flex gap-2">
            <button data-testid="household-join-btn" onClick={join} disabled={busy || !code.trim()} className="flex-1 rounded-full py-2.5 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60">
              {busy ? "Joining…" : "Join household"}
            </button>
            <button onClick={() => setMode("view")} className="rounded-full px-4 text-sm font-medium text-slate-500 hover:text-slate-900">Cancel</button>
          </div>
        </div>
      )}

      {hh && (
        <>
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{hh.name}</div>
            <div className="mt-2 space-y-1.5">
              {hh.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-900">{m.name}</span>
                  <span className="text-xs text-slate-400">{m.email}</span>
                </div>
              ))}
            </div>
          </div>

          {state.is_owner && hh.invite_code && (
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Invite code</div>
                  <div className="font-mono font-bold text-slate-900 tracking-widest text-base" data-testid="household-invite-code">{hh.invite_code}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid="household-copy-btn" onClick={() => copy(hh.invite_code)} className="rounded-full p-2 hover:bg-slate-200 text-slate-600" title="Copy">
                    {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                  </button>
                  <button data-testid="household-rotate-btn" onClick={rotate} disabled={busy} className="rounded-full p-2 hover:bg-slate-200 text-slate-600 disabled:opacity-50" title="Regenerate">
                    <RefreshCcw size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">Combined view</div>
              <div className="text-[11px] text-slate-500">Show everyone's spending together</div>
            </div>
            <button
              data-testid="household-mode-toggle"
              onClick={() => toggleMode()}
              role="switch"
              aria-checked={hhMode}
              className={`shrink-0 w-11 h-6 rounded-full relative transition-colors duration-200 ${hhMode ? "bg-emerald-500" : "bg-slate-300"}`}
            >
              <span className={`absolute top-0.5 ${hhMode ? "left-5" : "left-0.5"} w-5 h-5 rounded-full bg-white shadow transition-all duration-200`} />
            </button>
          </div>

          <button data-testid="household-leave-btn" onClick={leave} disabled={busy} className="mt-4 text-xs text-slate-500 hover:text-red-600 underline underline-offset-2 flex items-center gap-1">
            <LogOut size={12} /> Leave household
          </button>
        </>
      )}
    </div>
  );
}
